import { useCallback } from 'react';
import axios from 'axios';
import pLimit from 'p-limit';
import { notifyBugsnag } from 'utils/bugsnag';
import { useAccountContext } from 'utils/account/AccountContext';
import { useAuthContext } from 'utils/auth';
import { INGEST_SERVICE_HOSTNAME } from 'utils/env';

const limit = pLimit(10);

interface GetIngestUploadUrlsArgs {
  bundleId?: string;
  dataType?: string;
  files: { fileName: string }[];
  params?: Record<string, any>;
  prefix?: string;
  recordId: string;
}

interface PayloadData {
  bundleId: string;
  dataType?: string;
  files: { fileName: string }[];
  organizationId?: string;
  params?: Record<string, any>;
  prefix?: string;
  recordId: string;
}

export interface GetIngestUploadUrlsPayload {
  url: string;
  fields: Record<string, string>;
}

export function useGetIngestUploadUrls() {
  const { getAuthHeader } = useAuthContext();
  const { customer } = useAccountContext();
  const organizationId = customer?.id;

  // caller must handle cancellation by calling cancelSourceToken.cancel()
  const cancelTokenSource = axios.CancelToken.source();

  const getIngestUploadUrls = useCallback(
    async ({ bundleId, recordId, ...input }: GetIngestUploadUrlsArgs) => {
      try {
        const payloadData: PayloadData = {
          bundleId: bundleId ?? recordId,
          organizationId,
          recordId,
          ...input,
        };

        const { data } = await axios.post<{ payload: GetIngestUploadUrlsPayload[] }>(
          `${INGEST_SERVICE_HOSTNAME}/post`,
          payloadData,
          {
            cancelToken: cancelTokenSource.token,
            headers: {
              ...(await getAuthHeader()),
              'Content-Type': 'application/json',
              'X-SkySpecs-Customer': organizationId,
            },
          }
        );

        return { items: data.payload, payloadData };
      } catch (e) {
        notifyBugsnag(e.message);
        throw e;
      }
    },
    [cancelTokenSource, getAuthHeader, organizationId]
  );

  return { cancelTokenSource, getIngestUploadUrls };
}

interface CompleteIngestUploadArgs {
  payload: Record<string, any>;
}

export function useCompleteIngestUpload() {
  const { getAuthHeader } = useAuthContext();
  const { customer } = useAccountContext();
  const organizationId = customer?.id;

  const completeIngestUpload = useCallback(
    async ({ payload }: CompleteIngestUploadArgs) => {
      try {
        await axios.post(`${INGEST_SERVICE_HOSTNAME}/post/complete`, payload, {
          headers: {
            ...(await getAuthHeader()),
            'Content-Type': 'application/json',
            'X-SkySpecs-Customer': organizationId,
          },
        });
      } catch (e) {
        notifyBugsnag(e.message);
        throw e;
      }
    },
    [getAuthHeader, organizationId]
  );

  return completeIngestUpload;
}

interface UploadFile extends GetIngestUploadUrlsPayload {
  file: File;
}

interface UploadIngestFilesArgs {
  items: UploadFile[];
}

export async function uploadIngestFiles({ items }: UploadIngestFilesArgs) {
  const fileUploadPromises = items.map(item =>
    limit(() => {
      const { fields, file, url } = item;
      const formData = new FormData();

      Object.keys(fields).forEach(key => {
        formData.append(key, fields[key]);
      });

      // `file` must be appended at the end of the FormData
      // @see https://stackoverflow.com/a/15235866/2121598
      formData.append('file', file as File);

      return axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    })
  );

  try {
    await Promise.all(fileUploadPromises);
  } catch (e) {
    notifyBugsnag(`Failed to upload ingest file(s): ${e.message}`);
    throw e;
  }
}
